import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface Booking {
  id: string;
  booking_date: string;
  start_time: string;
  end_time: string;
  status: "pending" | "confirmed" | "completed" | "cancelled" | "no_show";
  total_price: number;
  notes: string | null;
  created_at: string;
  service: {
    id: string;
    name: string;
    duration_minutes: number;
  } | null;
  barber: {
    id: string;
    user_id: string;
    full_name?: string;
    avatar_url?: string | null;
  } | null;
  community: {
    id: string;
    name: string;
    address: string | null;
  } | null;
}

export function useCustomerBookings() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["customer-bookings", user?.id],
    queryFn: async () => {
      if (!user) return [];

      // First, fetch bookings with basic relations
      const { data: bookingsData, error } = await supabase
        .from("bookings")
        .select(`
          id,
          booking_date,
          start_time,
          end_time,
          status,
          total_price,
          notes,
          created_at,
          staff_id,
          service:services(id, name, duration_minutes),
          community:communities(id, name, address)
        `)
        .eq("customer_id", user.id)
        .order("booking_date", { ascending: false })
        .order("start_time", { ascending: false });

      if (error) throw error;
      if (!bookingsData || bookingsData.length === 0) return [];

      // Get staff profiles for the bookings
      const staffIds = [...new Set(bookingsData.map(b => b.staff_id).filter(Boolean))];
      
      let staffProfiles: Record<string, { id: string; user_id: string }> = {};
      if (staffIds.length > 0) {
        const { data: staffData } = await supabase
          .from("staff_profiles")
          .select("id, user_id")
          .in("id", staffIds);
        
        if (staffData) {
          staffProfiles = Object.fromEntries(staffData.map(s => [s.id, s]));
        }
      }

      // Get unique user_ids to fetch their names
      const staffUserIds = [...new Set(Object.values(staffProfiles).map(s => s.user_id))];

      // Fetch staff names from public_profiles
      let userProfiles: Record<string, { full_name: string; avatar_url: string | null }> = {};
      if (staffUserIds.length > 0) {
        const { data: profilesData } = await supabase
          .from("public_profiles")
          .select("id, full_name, avatar_url")
          .in("id", staffUserIds);

        if (profilesData) {
          userProfiles = Object.fromEntries(
            profilesData.map(p => [p.id, { full_name: p.full_name || "Staff", avatar_url: p.avatar_url }])
          );
        }
      }

      // Merge staff data into bookings
      return bookingsData.map(booking => {
        const staffProfile = booking.staff_id ? staffProfiles[booking.staff_id] : null;
        const userProfile = staffProfile ? userProfiles[staffProfile.user_id] : null;
        
        return {
          ...booking,
          barber: staffProfile ? {
            id: staffProfile.id,
            user_id: staffProfile.user_id,
            full_name: userProfile?.full_name || "Staff",
            avatar_url: userProfile?.avatar_url || null,
          } : null,
        };
      }) as Booking[];
    },
    enabled: !!user,
  });
}

export function useUpcomingBookings() {
  const { user } = useAuth();
  const today = new Date().toISOString().split("T")[0];

  return useQuery({
    queryKey: ["upcoming-bookings", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data: bookingsData, error } = await supabase
        .from("bookings")
        .select(`
          id,
          booking_date,
          start_time,
          end_time,
          status,
          total_price,
          notes,
          created_at,
          staff_id,
          service:services(id, name, duration_minutes),
          community:communities(id, name, address)
        `)
        .eq("customer_id", user.id)
        .gte("booking_date", today)
        .in("status", ["pending", "confirmed"])
        .order("booking_date", { ascending: true })
        .order("start_time", { ascending: true })
        .limit(500);

      if (error) throw error;
      if (!bookingsData || bookingsData.length === 0) return [];

      // Get staff profiles
      const staffIds = [...new Set(bookingsData.map(b => b.staff_id).filter(Boolean))];
      
      let staffProfiles: Record<string, { id: string; user_id: string }> = {};
      if (staffIds.length > 0) {
        const { data: staffData } = await supabase
          .from("staff_profiles")
          .select("id, user_id")
          .in("id", staffIds);
        
        if (staffData) {
          staffProfiles = Object.fromEntries(staffData.map(s => [s.id, s]));
        }
      }

      const staffUserIds = [...new Set(Object.values(staffProfiles).map(s => s.user_id))];

      // Fetch staff names from public_profiles
      let userProfiles: Record<string, { full_name: string; avatar_url: string | null }> = {};
      if (staffUserIds.length > 0) {
        const { data: profilesData } = await supabase
          .from("public_profiles")
          .select("id, full_name, avatar_url")
          .in("id", staffUserIds);

        if (profilesData) {
          userProfiles = Object.fromEntries(
            profilesData.map(p => [p.id, { full_name: p.full_name || "Staff", avatar_url: p.avatar_url }])
          );
        }
      }

      return bookingsData.map(booking => {
        const staffProfile = booking.staff_id ? staffProfiles[booking.staff_id] : null;
        const userProfile = staffProfile ? userProfiles[staffProfile.user_id] : null;
        
        return {
          ...booking,
          barber: staffProfile ? {
            id: staffProfile.id,
            user_id: staffProfile.user_id,
            full_name: userProfile?.full_name || "Staff",
            avatar_url: userProfile?.avatar_url || null,
          } : null,
        };
      }) as Booking[];
    },
    enabled: !!user,
  });
}

export function usePastBookings() {
  const { user } = useAuth();
  const today = new Date().toISOString().split("T")[0];

  return useQuery({
    queryKey: ["past-bookings", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data: bookingsData, error } = await supabase
        .from("bookings")
        .select(`
          id,
          booking_date,
          start_time,
          end_time,
          status,
          total_price,
          notes,
          created_at,
          staff_id,
          service:services(id, name, duration_minutes),
          community:communities(id, name, address)
        `)
        .eq("customer_id", user.id)
        .or(`booking_date.lt.${today},status.in.(completed,cancelled,no_show)`)
        .order("booking_date", { ascending: false })
        .order("start_time", { ascending: false })
        .limit(500);

      if (error) throw error;
      if (!bookingsData || bookingsData.length === 0) return [];

      // Get staff profiles
      const staffIds = [...new Set(bookingsData.map(b => b.staff_id).filter(Boolean))];
      
      let staffProfiles: Record<string, { id: string; user_id: string }> = {};
      if (staffIds.length > 0) {
        const { data: staffData } = await supabase
          .from("staff_profiles")
          .select("id, user_id")
          .in("id", staffIds);
        
        if (staffData) {
          staffProfiles = Object.fromEntries(staffData.map(s => [s.id, s]));
        }
      }

      const staffUserIds = [...new Set(Object.values(staffProfiles).map(s => s.user_id))];

      // Fetch staff names from public_profiles
      let userProfiles: Record<string, { full_name: string; avatar_url: string | null }> = {};
      if (staffUserIds.length > 0) {
        const { data: profilesData } = await supabase
          .from("public_profiles")
          .select("id, full_name, avatar_url")
          .in("id", staffUserIds);

        if (profilesData) {
          userProfiles = Object.fromEntries(
            profilesData.map(p => [p.id, { full_name: p.full_name || "Staff", avatar_url: p.avatar_url }])
          );
        }
      }

      return bookingsData.map(booking => {
        const staffProfile = booking.staff_id ? staffProfiles[booking.staff_id] : null;
        const userProfile = staffProfile ? userProfiles[staffProfile.user_id] : null;
        
        return {
          ...booking,
          barber: staffProfile ? {
            id: staffProfile.id,
            user_id: staffProfile.user_id,
            full_name: userProfile?.full_name || "Staff",
            avatar_url: userProfile?.avatar_url || null,
          } : null,
        };
      }) as Booking[];
    },
    enabled: !!user,
  });
}

export function useCancelBooking() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ bookingId, reason }: { bookingId: string; reason?: string }) => {
      if (reason && reason.length > 1000) {
        throw new Error("Cancellation reason must be 1000 characters or less");
      }
      const { error } = await supabase
        .from("bookings")
        .update({
          status: "cancelled",
          cancellation_reason: reason,
          cancelled_at: new Date().toISOString(),
          cancelled_by: user?.id,
        })
        .eq("id", bookingId)
        .eq("customer_id", user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["upcoming-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["past-bookings"] });
    },
  });
}
